import { useState, useEffect } from "react";
import { api, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

const ROLES = [
  { value: "admin", label: "Administrateur", desc: "Accès complet" },
  { value: "editor", label: "Éditeur", desc: "Créer & modifier (soumis à validation admin)" },
  { value: "journalist", label: "Journaliste", desc: "Créer & brouillons (soumis à validation)" },
  { value: "reader", label: "Lecteur", desc: "Aucun accès rédaction" },
];

const ROLE_CLS = {
  admin: "bg-rose-100 text-rose-700",
  editor: "bg-blue-100 text-blue-700",
  journalist: "bg-emerald-100 text-emerald-700",
  reader: "bg-slate-100 text-slate-500",
};

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "journalist" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { document.title = "Utilisateurs — Admin"; load(); }, []);

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/users/${userId}/role`, { role });
      toast.success("Rôle mis à jour");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("L'e-mail est obligatoire"); return; }
    setCreating(true);
    try {
      await api.post("/users", form);
      toast.success(`Compte ${ROLES.find((r) => r.value === form.role)?.label} créé`);
      setForm({ email: "", name: "", role: "journalist" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="p-5 sm:p-8 max-w-4xl">
      <h1 className="font-headline text-3xl font-black text-[#0F172A] mb-2">Utilisateurs & rôles</h1>
      <p className="meta-label text-slate-400 mb-6">Créez des administrateurs et des utilisateurs, et attribuez leurs rôles.</p>

      {/* Create user */}
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-5 mb-6">
        <h2 className="font-headline text-lg font-black text-[#0F172A] flex items-center gap-2 mb-4"><UserPlus size={18} className="text-[#E11D48]" /> Créer un compte</h2>
        <form onSubmit={createUser} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="meta-label text-slate-500">E-mail *</label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="prenom@exemple.com" data-testid="create-user-email" className="mt-1.5" />
          </div>
          <div>
            <label className="meta-label text-slate-500">Nom</label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Nom complet" data-testid="create-user-name" className="mt-1.5" />
          </div>
          <div>
            <label className="meta-label text-slate-500">Rôle</label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="mt-1.5" data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-4">
            <button type="submit" disabled={creating} data-testid="create-user-submit"
              className="inline-flex items-center gap-2 bg-[#0F172A] text-white px-5 py-2.5 rounded-full font-sub text-sm font-semibold hover:bg-[#1E293B] disabled:opacity-50">
              <UserPlus size={15} /> Créer le compte
            </button>
            <p className="font-body text-xs text-slate-400 mt-2 flex items-center gap-1.5">
              <Mail size={12} /> L'utilisateur se connecte ensuite via Google avec cet e-mail et retrouve automatiquement son rôle.
            </p>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="meta-label text-slate-400 border-b border-[#E2E8F0]">
                <th className="px-4 py-3 font-medium">Utilisateur</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell">Créé le</th>
                <th className="px-4 py-3 font-medium">Rôle actuel</th>
                <th className="px-4 py-3 font-medium">Modifier</th>
                <th className="px-4 py-3 font-medium text-right">Suppr.</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-[#F1F5F9]" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#0F172A] text-white flex items-center justify-center font-headline font-bold text-sm">{(u.name || u.email).charAt(0).toUpperCase()}</div>
                      <div className="min-w-0">
                        <p className="font-sub text-sm font-semibold text-[#0F172A] truncate">{u.name}</p>
                        <p className="meta-label text-slate-400 truncate">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell meta-label text-slate-500">{formatDate(u.created_at)}</td>
                  <td className="px-4 py-3"><span className={`meta-label px-2 py-1 rounded ${ROLE_CLS[u.role]}`}>{ROLES.find((r) => r.value === u.role)?.label || u.role}</span></td>
                  <td className="px-4 py-3">
                    <Select value={u.role} onValueChange={(v) => changeRole(u.user_id, v)}>
                      <SelectTrigger className="w-40 h-9 text-sm" data-testid={`role-select-${u.email}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map((r) => (
                          <SelectItem key={r.value} value={r.value}>
                            <span className="font-sub">{r.label}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.user_id !== me.user_id && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button title="Supprimer" data-testid={`delete-user-${u.email}`}
                            className="w-8 h-8 inline-flex items-center justify-center rounded hover:bg-rose-100 text-rose-500"><Trash2 size={15} /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
                            <AlertDialogDescription>Le compte « {u.name} » ({u.email}) sera supprimé et déconnecté.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteUser(u.user_id)} data-testid={`confirm-delete-user-${u.email}`} className="bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
        {ROLES.map((r) => (
          <div key={r.value} className="bg-white border border-[#E2E8F0] rounded-lg p-4">
            <span className={`meta-label px-2 py-1 rounded ${ROLE_CLS[r.value]}`}>{r.label}</span>
            <p className="font-body text-sm text-slate-500 mt-2">{r.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
